<script setup>
import { ref, reactive, computed } from "vue";
import * as Sentry from "@sentry/vue";
import ModalTemplate from "@/features/ModalTemplate.vue";
import BaseMedallion from "@/library/BaseMedallion.vue";
import BaseText from "@/library/BaseText.vue";
import BaseButton from "@/library/BaseButton.vue";
import BaseTextarea from "@/library/BaseTextarea.vue";
import { useToast } from "@/composables/useToast.js";

const toast = useToast();

const emit = defineEmits(["close"]);

defineProps({
  show: {
    type: Boolean,
    required: true,
  },
});

const formData = reactive({
  description: "",
});

const isSubmitting = ref(false);
const error = ref("");

const isFormValid = computed(() => {
  return formData.description.trim().length > 0;
});

const isButtonDisabled = computed(() => {
  return isSubmitting.value || !isFormValid.value;
});

const handleClose = () => {
  emit("close");
};

const handleSubmit = async () => {
  if (!formData.description.trim()) {
    error.value = "Please provide a description of your feedback.";
    return;
  }

  error.value = "";
  isSubmitting.value = true;

  try {
    Sentry.captureFeedback({
      message: formData.description,
      name: "Anonymous User",
      url: window.location.href,
      source: "ShareFeedbackModal",
    });

    toast.success("Feedback submitted successfully");
    handleClose();
  } catch {
    toast.error("Failed to submit feedback. Please try again.");
  } finally {
    isSubmitting.value = false;
  }
};

const resetForm = () => {
  formData.description = "";
  error.value = "";
};

const handleModalClose = () => {
  resetForm();
  handleClose();
};
</script>

<template>
  <ModalTemplate
    size="medium"
    :show="show"
    class="share-feedback-modal"
    @close="handleModalClose"
  >
    <template #body>
      <div class="share-feedback-modal__form-container">
        <div class="share-feedback-modal__title">
          <BaseMedallion
            class="share-feedback-modal__medallion"
            icon="text"
            color="cloaked"
          />

          <BaseText
            variant="headline-2-semibold"
            as="h2"
            class="share-feedback-modal__title-text"
          >
            Share feedback
          </BaseText>
        </div>

        <form
          class="share-feedback-modal__form"
          @submit.prevent="handleSubmit"
        >
          <BaseTextarea
            v-model="formData.description"
            title="Description"
            placeholder="Tell us about your experience, suggestions, or any issues you've encountered..."
            :rows="6"
            required
          />
          <div class="share-feedback-modal__actions">
            <BaseButton
              type="submit"
              variant="primary"
              :loading="isSubmitting"
              :disabled="isButtonDisabled"
              full-width
            >
              {{ isSubmitting ? "Submitting..." : "Submit feedback" }}
            </BaseButton>
          </div>
        </form>
      </div>
    </template>
  </ModalTemplate>
</template>

<style lang="scss" scoped>
.share-feedback-modal {
  &__title {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    margin-bottom: 24px;
  }

  &__medallion {
    margin-bottom: 8px;
  }

  &__title-text {
    text-align: center;
  }

  &__form {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__error {
    padding: 8px 12px;
    background-color: rgba($color-alert, 0.1);
    border: 1px solid $color-alert;
    border-radius: 8px;
  }

  &__error-text {
    color: $color-alert;
  }

  &__success {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    padding: 40px 20px;
    text-align: center;
  }

  &__success-text {
    color: $color-status-success;
  }

  &__success-description {
    color: $color-primary-70;
  }

  &__actions {
    margin-top: 8px;
  }
}
</style>
